import React, { useState, useCallback, useMemo } from 'react';
import { 
  AlertCircle, 
  CheckCircle2, 
  Edit2, 
  Save, 
  X, 
  Filter, 
  ChevronDown,
  ChevronUp,
  Search,
  Download,
  RefreshCw,
  ArrowUpDown,
  Brain,
  FileText,
  DollarSign,
  Calendar,
  Tag,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import api from '../utils/api';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Standard categories for dropdown
const STANDARD_CATEGORIES = [
  'Revenue',
  'Cost of Goods Sold',
  'Payroll',
  'Rent/Lease',
  'Utilities',
  'Insurance',
  'Marketing',
  'Supplies',
  'Professional Fees',
  'Travel',
  'Depreciation',
  'Interest Expense',
  'Other Operating Expense',
  'Uncategorized',
];

// Classification types
const CLASSIFICATION_TYPES = [
  { value: 'fixed', label: 'Fixed Cost' },
  { value: 'variable', label: 'Variable Cost' },
  { value: 'cogs', label: 'COGS' },
  { value: 'revenue', label: 'Revenue' },
  { value: 'payroll', label: 'Payroll' },
  { value: 'uncategorized', label: 'Uncategorized' },
];

interface Transaction {
  transaction_date: string;
  amount: number;
  description: string;
  category: string;
  sub_category?: string;
  classification: {
    type: string;
    confidence: number;
    auto_classified: boolean;
  };
  source_type: string;
  source_file: string;
  needs_review: boolean;
  anomaly: boolean;
  original_row?: number;
}

interface Period {
  year: number;
  month: number | null;
  transactions: Transaction[];
  gross_revenue: number;
  cogs: number;
  fixed_costs: number;
  variable_costs: number;
  net_profit: number;
}

interface CanonicalData {
  schema_version: string;
  processed_at: string;
  overall_confidence: number;
  validation_errors: string[];
  company_info: {
    name: string;
    business_type?: string;
  };
  business_type: string;
  periods: Period[];
  metadata: {
    source_files: string[];
    source_files_count: number;
  };
}

interface TransactionReviewProps {
  canonicalData: CanonicalData;
  onConfirm: (updatedData: CanonicalData) => void;
  onCancel?: () => void;
}

export default function TransactionReview({ 
  canonicalData, 
  onConfirm, 
  onCancel 
}: TransactionReviewProps) {
  // Flatten all transactions with period info
  const allTransactions = useMemo(() => {
    const txns: (Transaction & { periodKey: string; periodIndex: number; txnIndex: number })[] = [];
    canonicalData.periods.forEach((period, pIdx) => {
      period.transactions.forEach((txn, tIdx) => {
        txns.push({
          ...txn,
          periodKey: `${period.year}-${period.month?.toString().padStart(2, '0') || '00'}`,
          periodIndex: pIdx,
          txnIndex: tIdx,
        });
      });
    });
    return txns;
  }, [canonicalData]);

  // State
  const [editedData, setEditedData] = useState<CanonicalData>(canonicalData);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterNeedsReview, setFilterNeedsReview] = useState(false);
  const [filterLowConfidence, setFilterLowConfidence] = useState(false);
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [sortField, setSortField] = useState<'date' | 'amount' | 'description'>('date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [editingRow, setEditingRow] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<Partial<Transaction>>({});
  const [learnedMappings, setLearnedMappings] = useState<string[]>([]);
  const [savingMapping, setSavingMapping] = useState(false);

  // Stats
  const stats = useMemo(() => {
    let needsReview = 0;
    let lowConfidence = 0;
    let totalAmount = 0;
    
    allTransactions.forEach(txn => {
      if (txn.needs_review) needsReview++;
      if (txn.classification.confidence < 0.8) lowConfidence++;
      totalAmount += txn.amount;
    });
    
    return {
      total: allTransactions.length,
      needsReview,
      lowConfidence,
      totalAmount,
      periods: canonicalData.periods.length,
      sourceFiles: canonicalData.metadata.source_files_count,
    };
  }, [allTransactions, canonicalData]);

  // Filter and sort transactions
  const filteredTransactions = useMemo(() => {
    let filtered = [...allTransactions];
    
    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(txn => 
        txn.description.toLowerCase().includes(term) ||
        txn.category.toLowerCase().includes(term) ||
        txn.source_file.toLowerCase().includes(term)
      );
    }
    
    // Needs review filter
    if (filterNeedsReview) {
      filtered = filtered.filter(txn => txn.needs_review);
    }
    
    // Low confidence filter
    if (filterLowConfidence) {
      filtered = filtered.filter(txn => txn.classification.confidence < 0.8);
    }
    
    // Category filter
    if (filterCategory && filterCategory !== 'all') {
      filtered = filtered.filter(txn => txn.category === filterCategory);
    }
    
    // Sort
    filtered.sort((a, b) => {
      let cmp = 0;
      if (sortField === 'date') {
        cmp = new Date(a.transaction_date).getTime() - new Date(b.transaction_date).getTime();
      } else if (sortField === 'amount') {
        cmp = a.amount - b.amount;
      } else if (sortField === 'description') {
        cmp = a.description.localeCompare(b.description);
      }
      return sortDirection === 'asc' ? cmp : -cmp;
    });
    
    return filtered;
  }, [allTransactions, searchTerm, filterNeedsReview, filterLowConfidence, filterCategory, sortField, sortDirection]);

  // Start editing a row
  const startEdit = (txn: Transaction & { periodIndex: number; txnIndex: number }) => {
    const key = `${txn.periodIndex}-${txn.txnIndex}`;
    setEditingRow(key);
    setEditValues({
      category: txn.category,
      description: txn.description,
      amount: txn.amount,
      classification: { ...txn.classification },
    });
  };

  // Cancel edit
  const cancelEdit = () => {
    setEditingRow(null);
    setEditValues({});
  };

  // Save edit
  const saveEdit = async (txn: Transaction & { periodIndex: number; txnIndex: number }) => {
    const key = `${txn.periodIndex}-${txn.txnIndex}`;
    
    // Update the canonical data
    const updatedData = { ...editedData };
    const period = updatedData.periods[txn.periodIndex];
    const transaction = period.transactions[txn.txnIndex];
    
    // Apply edits
    if (editValues.category) transaction.category = editValues.category;
    if (editValues.description) transaction.description = editValues.description;
    if (editValues.amount !== undefined) transaction.amount = editValues.amount;
    if (editValues.classification) {
      transaction.classification = {
        ...transaction.classification,
        ...editValues.classification,
        auto_classified: false, // Mark as manually reviewed
      };
    }
    
    // Clear needs_review flag
    transaction.needs_review = false;
    
    // Check if category changed - learn the mapping
    if (editValues.category && editValues.category !== txn.category) {
      await learnMapping(txn.description, editValues.category);
    }
    
    setEditedData(updatedData);
    setEditingRow(null);
    setEditValues({});
  };

  // Learn mapping from correction
  const learnMapping = async (description: string, category: string) => {
    setSavingMapping(true);
    try {
      await api.post('/api/v1/classify/learn', {
        transaction: {
          description: description,
          amount: 0,
        },
        correct_category: category,
      }, { showErrorToast: false });
      
      setLearnedMappings(prev => [...prev, `${description.substring(0, 30)}... → ${category}`]);
    } catch (error) {
      console.error('Failed to learn mapping:', error);
    } finally {
      setSavingMapping(false);
    }
  };

  // Toggle sort
  const toggleSort = (field: 'date' | 'amount' | 'description') => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // Approve all visible
  const approveAllVisible = () => {
    const updatedData = { ...editedData };
    
    filteredTransactions.forEach(txn => {
      const transaction = updatedData.periods[txn.periodIndex].transactions[txn.txnIndex];
      transaction.needs_review = false;
    });
    
    setEditedData(updatedData);
  };

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  // Format date
  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="space-y-6">
      {/* Header Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-xl flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Transaction Review
              </CardTitle>
              <CardDescription>
                Review and correct parsed transactions before calculation
              </CardDescription>
            </div>
            <div className="flex gap-2">
              {onCancel && (
                <Button variant="outline" onClick={onCancel}>
                  Cancel
                </Button>
              )}
              <Button onClick={() => onConfirm(editedData)} className="bg-green-600 hover:bg-green-700">
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Confirm & Calculate
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="bg-blue-50 rounded-lg p-3">
              <div className="text-sm text-blue-600 font-medium">Total Transactions</div>
              <div className="text-2xl font-bold text-blue-900">{stats.total}</div>
            </div>
            <div className={`rounded-lg p-3 ${stats.needsReview > 0 ? 'bg-amber-50' : 'bg-green-50'}`}>
              <div className={`text-sm font-medium ${stats.needsReview > 0 ? 'text-amber-600' : 'text-green-600'}`}>
                Needs Review
              </div>
              <div className={`text-2xl font-bold ${stats.needsReview > 0 ? 'text-amber-900' : 'text-green-900'}`}>
                {stats.needsReview}
              </div>
            </div>
            <div className="bg-purple-50 rounded-lg p-3">
              <div className="text-sm text-purple-600 font-medium">Periods</div>
              <div className="text-2xl font-bold text-purple-900">{stats.periods}</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="text-sm text-gray-600 font-medium">Source Files</div>
              <div className="text-2xl font-bold text-gray-900">{stats.sourceFiles}</div>
            </div>
            <div className="bg-emerald-50 rounded-lg p-3">
              <div className="text-sm text-emerald-600 font-medium">Net Amount</div>
              <div className="text-2xl font-bold text-emerald-900">
                {formatCurrency(stats.totalAmount)}
              </div>
            </div>
          </div>
          
          {/* Confidence Badge */}
          <div className="mt-4 flex items-center gap-2">
            <Brain className="w-4 h-4 text-gray-500" />
            <span className="text-sm text-gray-600">Overall Confidence:</span>
            <Badge variant={canonicalData.overall_confidence > 0.9 ? 'default' : 'secondary'}>
              {(canonicalData.overall_confidence * 100).toFixed(1)}%
            </Badge>
          </div>
          
          {/* Learned Mappings */}
          {learnedMappings.length > 0 && (
            <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center gap-2 text-green-800 text-sm font-medium">
                <CheckCircle2 className="w-4 h-4" />
                System learned {learnedMappings.length} new mapping(s)
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Filters Card */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap items-center gap-4">
            {/* Search */}
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search transactions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            
            {/* Category Filter */}
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {STANDARD_CATEGORIES.map(cat => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Toggle Filters */}
            <Button
              variant={filterNeedsReview ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilterNeedsReview(!filterNeedsReview)}
            >
              <AlertCircle className="w-4 h-4 mr-1" />
              Needs Review ({stats.needsReview})
            </Button>
            
            <Button
              variant={filterLowConfidence ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilterLowConfidence(!filterLowConfidence)}
            >
              Low Confidence ({stats.lowConfidence})
            </Button>
            
            {/* Bulk Actions */}
            {filteredTransactions.some(t => t.needs_review) && (
              <Button
                variant="outline"
                size="sm"
                onClick={approveAllVisible}
                className="text-green-600 border-green-300 hover:bg-green-50"
              >
                <CheckCircle2 className="w-4 h-4 mr-1" />
                Approve All Visible
              </Button>
            )}
          </div>
          
          {/* Active Filters */}
          {(searchTerm || filterNeedsReview || filterLowConfidence || filterCategory !== 'all') && (
            <div className="flex items-center gap-2 mt-3 text-sm text-gray-600">
              <Filter className="w-4 h-4" />
              Showing {filteredTransactions.length} of {stats.total} transactions
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => {
                  setSearchTerm('');
                  setFilterNeedsReview(false);
                  setFilterLowConfidence(false);
                  setFilterCategory('all');
                }}
              >
                Clear Filters
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Transactions Table */}
      <Card>
        <CardContent className="pt-6">
          <div className="rounded-lg border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="w-[100px]">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => toggleSort('date')}
                      className="font-semibold"
                    >
                      <Calendar className="w-4 h-4 mr-1" />
                      Date
                      <ArrowUpDown className="w-3 h-3 ml-1" />
                    </Button>
                  </TableHead>
                  <TableHead>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => toggleSort('description')}
                      className="font-semibold"
                    >
                      Description
                      <ArrowUpDown className="w-3 h-3 ml-1" />
                    </Button>
                  </TableHead>
                  <TableHead className="w-[120px]">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => toggleSort('amount')}
                      className="font-semibold"
                    >
                      <DollarSign className="w-4 h-4 mr-1" />
                      Amount
                      <ArrowUpDown className="w-3 h-3 ml-1" />
                    </Button>
                  </TableHead>
                  <TableHead className="w-[150px]">
                    <div className="flex items-center gap-1 font-semibold">
                      <Tag className="w-4 h-4" />
                      Category
                    </div>
                  </TableHead>
                  <TableHead className="w-[100px]">Classification</TableHead>
                  <TableHead className="w-[80px]">Confidence</TableHead>
                  <TableHead className="w-[100px]">Source</TableHead>
                  <TableHead className="w-[80px]">Status</TableHead>
                  <TableHead className="w-[100px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTransactions.map((txn) => {
                  const rowKey = `${txn.periodIndex}-${txn.txnIndex}`;
                  const isEditing = editingRow === rowKey;
                  
                  return (
                    <TableRow 
                      key={rowKey}
                      className={`
                        ${txn.needs_review ? 'bg-amber-50' : ''}
                        ${txn.classification.confidence < 0.8 ? 'bg-purple-50' : ''}
                        ${isEditing ? 'bg-blue-50' : ''}
                      `}
                    >
                      <TableCell className="font-mono text-sm">
                        {formatDate(txn.transaction_date)}
                      </TableCell>
                      <TableCell>
                        {isEditing ? (
                          <Input
                            value={editValues.description || ''}
                            onChange={(e) => setEditValues({ ...editValues, description: e.target.value })}
                            className="h-8"
                          />
                        ) : (
                          <span className="line-clamp-1" title={txn.description}>
                            {txn.description}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className={`font-mono font-medium ${txn.amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {isEditing ? (
                          <Input
                            type="number"
                            step="0.01"
                            value={editValues.amount ?? 0}
                            onChange={(e) => setEditValues({ ...editValues, amount: parseFloat(e.target.value) })}
                            className="h-8 w-24"
                          />
                        ) : (
                          formatCurrency(txn.amount)
                        )}
                      </TableCell>
                      <TableCell>
                        {isEditing ? (
                          <Select 
                            value={editValues.category || txn.category} 
                            onValueChange={(v) => setEditValues({ ...editValues, category: v })}
                          >
                            <SelectTrigger className="h-8">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {STANDARD_CATEGORIES.map(cat => (
                                <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <Badge variant="outline" className="font-normal">
                            {txn.category}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {isEditing ? (
                          <Select 
                            value={editValues.classification?.type || txn.classification.type} 
                            onValueChange={(v) => setEditValues({ 
                              ...editValues, 
                              classification: { ...editValues.classification, type: v } as any
                            })}
                          >
                            <SelectTrigger className="h-8">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {CLASSIFICATION_TYPES.map(ct => (
                                <SelectItem key={ct.value} value={ct.value}>{ct.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <span className="text-xs text-gray-600 capitalize">
                            {txn.classification.type}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge 
                          variant={txn.classification.confidence >= 0.9 ? 'default' : 
                                   txn.classification.confidence >= 0.8 ? 'secondary' : 'destructive'}
                        >
                          {(txn.classification.confidence * 100).toFixed(0)}%
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs text-gray-500">
                        {txn.source_file.length > 15 
                          ? txn.source_file.slice(0, 12) + '...' 
                          : txn.source_file}
                      </TableCell>
                      <TableCell>
                        {txn.needs_review ? (
                          <Badge variant="outline" className="border-amber-400 text-amber-700 bg-amber-50">
                            Review
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="border-green-400 text-green-700 bg-green-50">
                            OK
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {isEditing ? (
                          <div className="flex gap-1">
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              onClick={() => saveEdit(txn)}
                              className="h-7 w-7 p-0 text-green-600"
                            >
                              <Save className="w-4 h-4" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              onClick={cancelEdit}
                              className="h-7 w-7 p-0 text-gray-400"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        ) : (
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            onClick={() => startEdit(txn)}
                            className="h-7 w-7 p-0"
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
          
          {/* Empty State */}
          {filteredTransactions.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              <Search className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No transactions match your filters</p>
              <Button 
                variant="link" 
                onClick={() => {
                  setSearchTerm('');
                  setFilterNeedsReview(false);
                  setFilterLowConfidence(false);
                  setFilterCategory('all');
                }}
              >
                Clear all filters
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Footer Actions */}
      <div className="flex items-center justify-between sticky bottom-0 bg-white border-t p-4 -mx-4 -mb-4">
        <div className="text-sm text-gray-600">
          {stats.needsReview === 0 ? (
            <span className="flex items-center gap-2 text-green-600 font-medium">
              <CheckCircle2 className="w-4 h-4" />
              All transactions reviewed
            </span>
          ) : (
            <span className="flex items-center gap-2 text-amber-600">
              <AlertCircle className="w-4 h-4" />
              {stats.needsReview} transaction(s) still need review
            </span>
          )}
        </div>
        <div className="flex gap-3">
          {onCancel && (
            <Button variant="outline" onClick={onCancel}>
              Cancel
            </Button>
          )}
          <Button 
            onClick={() => onConfirm(editedData)} 
            className="bg-green-600 hover:bg-green-700"
            disabled={savingMapping}
          >
            <CheckCircle2 className="w-4 h-4 mr-2" />
            Confirm & Run Calculation
          </Button>
        </div>
      </div>
    </div>
  );
}
